"use client";
import { useMemo, useState, useRef, MouseEvent, ReactNode } from "react";
import { format } from "date-fns";
import {
   MoreHorizontal,
   Edit2,
   Trash2,
   Share2,
   Pin,
   PinOff,
   MessageCircle
} from "lucide-react";
import { useTranslations } from "next-intl";
import { useParams } from "next/navigation";
import { useRouter } from "@/i18n/navigation";
import { cn } from "@/app/lib/utils";
import {
   Input,
   Button,
   DropdownMenu,
   DropdownMenuContent,
   DropdownMenuItem,
   DropdownMenuTrigger,
   AlertDialogConfirm,
   CreatePublicLinkChatHistory
} from "@/app/components/ui";
import { useStateHistoryChatPage } from "@/app/store/chat";
import { ChatHistory } from "@/types";
import { api_url } from "@/app/api";
import { fetchWithAuth } from "@/app/lib/authInterceptor";
import { GRAPHQL_ERROR_CODE, KEY, METHOD_TYPE } from "@/app/lib/enums";
import {
   CHAT_TITLE_PREVIEW_LENGTH,
   MAX_PINNED_CHAT_HISTORIES
} from "@/app/lib/constants";
import { useToast } from "@/app/hooks";

interface ChatHistoryItemProps {
   item: ChatHistory;
   shareKey?: string;
   studentId?: string;
   pinDisabled?: boolean;
   onItemPatch?: (item: Partial<ChatHistory>) => void;
   onItemRemove?: (id: string) => void;
}

export const ChatHistoryItem: React.FC<ChatHistoryItemProps> = (
   props: ChatHistoryItemProps
) => {
   const { item, shareKey, studentId, pinDisabled, onItemPatch, onItemRemove } = props;
   const router = useRouter();
   const params = useParams();
   const { toast } = useToast();
   const [showDeleteDialog, setShowDeleteDialog] = useState(false);
   const [showShareDialog, setShowShareDialog] = useState(false);
   const [showPinLimitDialog, setShowPinLimitDialog] = useState(false);
   const translate = useTranslations("Home");
   const inputTitleRef = useRef<HTMLInputElement>(null);
   const {
      historyItem,
      setHistoryItem,
      itemEdit,
      setItemEdit,
      setTemporaryChat,
      loadingChat,
      hasActiveGeneration
   } = useStateHistoryChatPage();
   const isSelected = useMemo(
      () => historyItem?.id === item.id,
      [historyItem?.id, item.id]
   );
   const shareActionsDisabled =
      hasActiveGeneration(item.id) || (isSelected && loadingChat);
   const isPinned = Boolean(item.pinnedAt);
   const pinActionLabel = translate(isPinned ? "unpin" : "pin");
   // bg-transparent: show a bare icon, not the ghost variant's button box
   const actionButtonClassName = cn(
      "flex h-7 w-7 items-center justify-center bg-transparent hover:bg-transparent",
      {
         "opacity-100": isSelected,
         // Touch screens have no hover, so the buttons stay visible below md
         "opacity-100 md:opacity-0 md:transition-opacity md:group-hover:opacity-100 md:focus-visible:opacity-100":
            !isSelected
      }
   );
   const pinIconClassName = "h-4 w-4 text-muted-foreground hover:text-foreground";
   // Cut by code point, not UTF-16 unit, so a trailing emoji never becomes a lone surrogate
   const titleCharacters = Array.from(item.title);
   const titlePreview =
      titleCharacters.length > CHAT_TITLE_PREVIEW_LENGTH
         ? `${titleCharacters.slice(0, CHAT_TITLE_PREVIEW_LENGTH).join("")}…`
         : item.title;
   function handleDeleteClick(e: MouseEvent<HTMLElement>) {
      e.stopPropagation();
      setShowDeleteDialog(true);
   }

   async function handleDeleteConfirm(e: MouseEvent<HTMLElement>) {
      e.stopPropagation();
      const response = await fetchWithAuth(api_url.chat_history_url, {
         method: METHOD_TYPE.DELETE,
         body: JSON.stringify({
            chatHistoryId: item.id
         })
      });
      if (!response.ok) {
         return toast({
            title: translate("error", {
               error: "Failed to delete history item"
            }),
            description: translate("error", { error: response.statusText }),
            variant: "destructive"
         });
      }
      const { data } = await response.json();
      data &&
         toast({
            title: translate("success"),
            description: translate("historyDeletedSuccessfully"),
            variant: "success",
            duration: 3000
         });
      if (isSelected) {
         setHistoryItem(null);
         router.push("/", { scroll: false });
      }

      onItemRemove?.(item.id);
   }
   async function handleUpdateTitle() {
      const newTitle = inputTitleRef.current?.value?.trim();
      setItemEdit(null);
      if (!newTitle || newTitle === item.title) return;

      const response = await fetchWithAuth(api_url.chat_history_url, {
         method: METHOD_TYPE.PATCH,
         body: JSON.stringify({
            chatHistoryId: item.id,
            title: newTitle
         })
      });

      if (!response.ok) {
         return toast({
            title: translate("error", {
               error: "Failed to update history item"
            }),
            description: translate("error", { error: response.statusText }),
            variant: "destructive"
         });
      }
      const { data } = await response.json();
      data &&
         toast({
            title: translate("success"),
            description: translate("historyUpdatedSuccessfully"),
            variant: "success",
            duration: 3000
         });

      onItemPatch?.({ id: item.id, title: newTitle });
   }
   async function handleTogglePin() {
      const nextIsPinned = !isPinned;
      if (nextIsPinned && pinDisabled) {
         return setShowPinLimitDialog(true);
      }

      const response = await fetchWithAuth(api_url.chat_history_pin_url, {
         method: METHOD_TYPE.PATCH,
         body: JSON.stringify({
            chatHistoryId: item.id,
            isPinned: nextIsPinned
         })
      });
      // Rate-limit and auth rejections can come back without a JSON body
      const { data, code } = await response.json().catch(() => ({}));

      if (!response.ok) {
         // The count can drift from what pinDisabled saw (another tab pinned in between)
         if (code === GRAPHQL_ERROR_CODE.PIN_LIMIT_EXCEEDED) {
            return setShowPinLimitDialog(true);
         }
         return toast({
            title: translate("error", { error: translate("failedToPin") }),
            description: translate("tryAgain"),
            variant: "destructive"
         });
      }

      toast({
         title: translate("success"),
         description: translate(nextIsPinned ? "chatPinned" : "chatUnpinned"),
         variant: "success",
         duration: 3000
      });
      // Patched in place instead of refetching so infinite scroll keeps its position
      onItemPatch?.(data);
   }
   const handleSelectHistory = () => {
      if (isSelected) return;
      setTemporaryChat(false);
      setHistoryItem({ ...item });
      if (shareKey) {
         router.push(`/c/${item.id}`);
      } else {
         const currentUrl = window.location.href;
         const url = studentId
            ? (() => {
                 // Check if chatHistoryId already exists in URL
                 if (currentUrl.includes("chatHistoryId=")) {
                    // Replace existing chatHistoryId value
                    return currentUrl.replace(
                       /chatHistoryId=[^&]*/,
                       `chatHistoryId=${item.id}`
                    );
                 } else {
                    // Add new chatHistoryId parameter
                    return `${currentUrl}${
                       currentUrl.includes("?") ? "&" : "?"
                    }chatHistoryId=${item.id}`;
                 }
              })()
            : `/${params.locale}/c/${item.id}`;

         if (studentId) {
            window.history.replaceState(null, "", url);
         } else {
            window.history.pushState(null, "", url);
         }
      }
   };
   const handleItemClick = (event: MouseEvent<HTMLDivElement>) => {
      // Portal clicks still bubble here through the React tree
      if (!event.currentTarget.contains(event.target as Node)) return;

      handleSelectHistory();
   };
   return (
      <div
         className={cn(
            "relative w-full max-w-full min-w-0 overflow-hidden rounded-lg p-2 pr-16 cursor-pointer transition-colors group",
            {
               "bg-primary/10 border-l-2 border-l-primary": isSelected,
               "hover:bg-primary/10": !isSelected
            }
         )}
         onClick={handleItemClick}
      >
         <div className="flex w-full max-w-full min-w-0 items-start gap-2 overflow-hidden">
            {isPinned && (
               <MessageCircle
                  className={cn("mt-0.5 h-4 w-4 shrink-0", {
                     "text-primary": isSelected,
                     "text-muted-foreground": !isSelected
                  })}
               />
            )}
            <div className="block w-full max-w-full min-w-0 overflow-hidden">
               {itemEdit?.id === item.id ? (
                  <Input
                     ref={inputTitleRef}
                     type="text"
                     defaultValue={itemEdit.title}
                     className="font-semibold"
                     onClick={(e) => e.stopPropagation()}
                     onBlur={() => handleUpdateTitle()}
                     onKeyDown={(e) => {
                        if (e.key === KEY.Enter) {
                           handleUpdateTitle();
                        }
                        if (e.key === KEY.Escape) {
                           setItemEdit(null);
                        }
                     }}
                  />
               ) : (
                  <h3
                     className={cn(
                        "block w-full max-w-full overflow-hidden text-ellipsis whitespace-nowrap font-medium text-sm",
                        {
                           "text-primary": isSelected,
                           "text-foreground": !isSelected
                        }
                     )}
                  >
                     {item.title}
                  </h3>
               )}

               <div className="overflow-hidden text-ellipsis whitespace-nowrap text-xs text-muted-foreground">
                  {format(item?.createdAt ?? new Date(), "yyyy-MM-dd hh:mm")}
               </div>
            </div>
         </div>
         <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-0.5">
            <Button
               variant="ghost"
               title={pinActionLabel}
               aria-label={pinActionLabel}
               className={actionButtonClassName}
               onClick={(e) => {
                  e.stopPropagation();
                  handleTogglePin();
               }}
            >
               {isPinned ? (
                  <PinOff className={pinIconClassName} />
               ) : (
                  <Pin className={pinIconClassName} />
               )}
            </Button>
            <DropdownMenu>
               <DropdownMenuTrigger asChild>
                  <Button
                     variant="ghost"
                     className={actionButtonClassName}
                     onClick={(e) => e.stopPropagation()}
                  >
                     <MoreHorizontal className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                  </Button>
               </DropdownMenuTrigger>
               <DropdownMenuContent align="end" className="w-[160px]">
                  <DropdownMenuItem
                     onClick={(e) => {
                        e.stopPropagation();
                        handleTogglePin();
                     }}
                     className="cursor-pointer"
                  >
                     {isPinned ? (
                        <PinOff className="h-4 w-4 mr-2" />
                     ) : (
                        <Pin className="h-4 w-4 mr-2" />
                     )}
                     <span>{pinActionLabel}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                     onClick={(e) => {
                        e.stopPropagation();
                        setItemEdit({ ...item });
                        setTimeout(() => {
                           inputTitleRef.current?.focus();
                        }, 700);
                     }}
                     className="cursor-pointer"
                  >
                     <Edit2 className="h-4 w-4 mr-2" />
                     <span>{translate("rename")}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                     onClick={(e) => {
                        e.stopPropagation();
                        setShowShareDialog(true);
                     }}
                     className="cursor-pointer"
                  >
                     <Share2 className="h-4 w-4 mr-2" />
                     <span>{translate("share")}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                     className="text-red-500 focus:text-red-700 cursor-pointer"
                     onClick={handleDeleteClick}
                  >
                     <Trash2 className="h-4 w-4 mr-2" />
                     <span>{translate("delete")}</span>
                  </DropdownMenuItem>
               </DropdownMenuContent>
            </DropdownMenu>
         </div>

         {showPinLimitDialog && (
            <AlertDialogConfirm
               title={translate("pinLimitReachedTitle", {
                  max: MAX_PINNED_CHAT_HISTORIES
               })}
               description={translate("pinLimitReachedDescription")}
               confirmText={translate("ok")}
               isOpen={showPinLimitDialog}
               setOpen={setShowPinLimitDialog}
               onConfirm={() => setShowPinLimitDialog(false)}
               showCloseButton={false}
               className="max-w-[420px]"
               headerClassName="items-center sm:text-center"
               titleClassName="text-xl font-bold"
               footerClassName="sm:justify-center"
               confirmButtonClassName="w-full h-11 text-base font-medium"
            />
         )}

         {showDeleteDialog && (
            <AlertDialogConfirm
               title={translate("deleteHistoryTitle")}
               description={
                  <span className="break-words">
                     {translate.rich("deleteHistoryConfirm", {
                        title: titlePreview,
                        b: (chunks: ReactNode) => <strong>{chunks}</strong>
                     })}
                  </span>
               }
               cancelText={translate("cancel")}
               confirmText={translate("delete")}
               confirmButtonClassName="bg-red-500 text-white hover:bg-red-600"
               isOpen={showDeleteDialog}
               setOpen={setShowDeleteDialog}
               onConfirm={handleDeleteConfirm}
               showCloseButton={false}
            />
         )}

         {showShareDialog && (
            <CreatePublicLinkChatHistory
               item={item}
               isOpen={showShareDialog}
               setOpen={setShowShareDialog}
               actionsDisabled={shareActionsDisabled}
               onShareStateChange={onItemPatch}
            />
         )}
      </div>
   );
};
